import { Colors } from "../../../utils/colors";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../../Containers/PopupModalContainer";
import RectangleButton from "../../Buttons/RectangleButton";
import { useCallback, useMemo, useState } from "react";
import { AccountData, NotificationType, ThemeType } from "@markit/common.types";
import {
  getAccountState,
  toggleInCreatorMode,
} from "../../../redux/slices/accountSlice";
import { useDispatch, useSelector } from "react-redux";
import MiniProfileItem from "../../DisplayItem/ProfileItem/MiniProfileItem";
import { Icon } from "@iconify/react";
import { useMultiAccountManager } from "../../../hooks/useMultiAccountManager";
import { useNavigate } from "react-router-dom";
import { NavigationId } from "../../../navigation/AppParamList";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import { DataLoaders } from "../../../redux/slices/dataSlice";
import { LightTheme } from "../../../hooks/useTheme";
import { LoginStage } from "../../../screens/Login/LoginWelcome";
import { hasSubscription } from "@markit/common.utils";

type ProfileSwitchAccountModalProps = {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
};

const ProfileSwitchAccountModal = (props: ProfileSwitchAccountModalProps) => {
  const { isVisible, setIsVisible } = props;
  const { additionalAccountData, accountData, originalAccountAdmin } =
    useSelector(getAccountState).account;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { switchToAdditionalAccount } = useMultiAccountManager();
  const [selectedAccount, setSelectedAccount] =
    useState<AccountData>(accountData);

  const allAccounts = useMemo(() => {
    return [accountData, ...additionalAccountData];
  }, [accountData, additionalAccountData]);

  // Prevent selecting an account that doesn't match the mode that is being switched to.
  // Exception is switching to creator mode and selecting a phone account with a subscription
  const disableContinue = useMemo(
    () =>
      accountData.inCreatorMode
        ? !selectedAccount.phoneNumber
        : !selectedAccount.email && !hasSubscription(selectedAccount),
    [accountData.inCreatorMode, selectedAccount]
  );

  const switchAccountOnPress = useCallback(async () => {
    const currentAccountMode = accountData.inCreatorMode;
    let navToCreatorHome = false;
    // If you continue with the same as current account, just switch modes
    if (accountData.uid === selectedAccount.uid) {
      dispatch(toggleInCreatorMode(accountData.uid, accountData.inCreatorMode));
      showNotificationBanner(
        dispatch,
        "Switched",
        NotificationType.AFFIRMATIVE
      );
      navToCreatorHome = !selectedAccount.inCreatorMode;
    } else {
      // If different account, handle account switching
      await switchToAdditionalAccount(selectedAccount.uid, true);
      // Switch the selected account mode if equal to the current account mode
      if (currentAccountMode === selectedAccount.inCreatorMode) {
        dispatch(toggleInCreatorMode(selectedAccount.uid, currentAccountMode));
        navToCreatorHome = !currentAccountMode;
      } else {
        navToCreatorHome = selectedAccount.inCreatorMode;
      }
    }
    if (navToCreatorHome) {
      navigate("/home");
    } else {
      dispatch(DataLoaders.user(selectedAccount.uid));
      navigate(`/u/${selectedAccount.username}`, {
        state: { username: selectedAccount.username },
      });
    }
    setIsVisible(false);
  }, [
    accountData.inCreatorMode,
    accountData.uid,
    dispatch,
    navigate,
    selectedAccount.inCreatorMode,
    selectedAccount.uid,
    selectedAccount.username,
    setIsVisible,
    switchToAdditionalAccount,
  ]);

  const addAccountOnPress = useCallback(() => {
    if (accountData.isAdmin && originalAccountAdmin) {
      alert(
        "Switch off of the admin mode viewing account before adding a new account."
      );
      return;
    }
    if (accountData.inCreatorMode) {
      navigate(NavigationId.SIGN_IN, {
        state: { preLoginStage: LoginStage.PHONE },
      });
    } else {
      navigate(NavigationId.WELCOME + "?addAccount=true");
    }
    setIsVisible(false);
  }, [
    accountData.inCreatorMode,
    accountData.isAdmin,
    originalAccountAdmin,
    navigate,
    setIsVisible,
  ]);

  return isVisible ? (
    <PopupModalContainer
      headerComp={
        <div className="ColumnNormal" style={{ gap: 7 }}>
          <span className="sectionTitle">
            Switch to {accountData.inCreatorMode ? "Subscriber" : "Creator"}{" "}
            Mode
          </span>
          <span style={{ fontWeight: 400, color: Colors.GRAY1, fontSize: 12 }}>
            You must switch to{" "}
            {accountData.inCreatorMode ? "a phone-based" : "an email-based"}{" "}
            account to continue to{" "}
            {accountData.inCreatorMode ? "subscriber" : "creator"} mode. Select
            or create{" "}
            {accountData.inCreatorMode ? "a phone-based" : "an email-based"}{" "}
            account.
          </span>
        </div>
      }
      valueComp={
        <div className="ColumnNormal" style={{ gap: 10 }}>
          <div>
            <span className="smallBodySubtext">Switch Account</span>
            {allAccounts.map((account) => (
              <div
                key={account.uid}
                style={{ paddingBlock: 7 }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                <MiniProfileItem
                  item={account}
                  onItemPress={() => setSelectedAccount(account)}
                  themeType={ThemeType.Light}
                  rightIcon={
                    account.uid === selectedAccount.uid ? (
                      <Icon
                        icon="ion:checkmark"
                        height={18}
                        style={{ color: Colors.BLACK }}
                      />
                    ) : undefined
                  }
                />
              </div>
            ))}
            <div
              className="AlignedRowSpacedSelect"
              style={{ paddingBlock: 12 }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              onClick={addAccountOnPress}
            >
              <span style={{ fontSize: 13, fontWeight: 500 }}>
                Add {accountData.inCreatorMode ? "Phone" : "Email"} Account
              </span>
              <Icon
                icon="ion:plus"
                height={18}
                style={{ color: Colors.BLACK }}
              />
            </div>
          </div>
          <RectangleButton
            buttonLabel={
              disableContinue
                ? `Select ${
                    accountData.inCreatorMode ? "Phone" : "Email"
                  } Account`
                : "Continue"
            }
            onPress={switchAccountOnPress}
            altColor={Colors.BLACK}
            altTextColor={Colors.WHITE}
            containerStyles={{ paddingInline: 14, paddingBlock: 12 }}
            disabled={disableContinue}
          />
        </div>
      }
      noExit
      theme={LightTheme}
      closeOnOutsidePress
      closeModal={() => setIsVisible(false)}
      modalContentWidth={PopupModalContainerWidth.SMALL}
    />
  ) : null;
};

export default ProfileSwitchAccountModal;
